import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Barcode, AlertTriangle } from 'lucide-react';
import { extractUniqueEpcTags, parseEpcStream } from './receivedUtils';
import { XMarkIcon } from '@heroicons/react/24/outline';

const KIND_COPY = {
  initial: {
    title: 'Scan box',
    description:
      'Point the Zebra scanner at the box and pull the trigger. EPC reads stream into the box below — duplicates are dropped automatically. Keep scanning until the counter stops moving, then process.',
    submit: 'Process scan',
  },
  rescan: {
    title: 'Re-scan box',
    description: 'Reposition the reader and re-scan the box. Duplicates are dropped automatically.',
    submit: 'Process re-scan',
  },
  verify: {
    title: 'Verification re-scan',
    description: 'Re-scan the received box to verify its contents.',
    submit: 'Process & amend',
  },
};

function ScanCaptureDialog({ box, poDbId, kind, onSubmit, onClose }) {
  const textareaRef = useRef(null);
  const logRef = useRef(null);
  const [raw, setRaw] = useState('');
  const [counts, setCounts] = useState({ raw: 0, unique: 0, dupes: 0 });
  const [captured, setCaptured] = useState([]);
  const [showOverConfirm, setShowOverConfirm] = useState(false);
  const [showMixedConfirm, setShowMixedConfirm] = useState(false);
  const [allowMixedSku, setAllowMixedSku] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [isValidating, setIsValidating] = useState(false);

  const open = !!box;
  const copy = KIND_COPY[kind] || KIND_COPY.initial;
  const isContainer = box?.inboundType === 'container';
  const isRealBox =
    box?.inboundType === 'box' &&
    Number.isInteger(Number(box?.id)) &&
    Number(box?.id) > 0;

  const recompute = useCallback((value) => {
    const tags = parseEpcStream(value);
    const unique = new Set(tags);
    setCounts({ raw: tags.length, unique: unique.size, dupes: tags.length - unique.size });
    setCaptured(Array.from(unique));
  }, []);

  useEffect(() => {
    if (open) {
      setRaw('');
      setCaptured([]);
      setCounts({ raw: 0, unique: 0, dupes: 0 });
      setShowOverConfirm(false);
      setShowMixedConfirm(false);
      setAllowMixedSku(false);
      setValidationResult(null);
      setIsValidating(false);
      setTimeout(() => textareaRef.current?.focus(), 50);
    }
  }, [open, box?.id, kind]);

  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [captured]);

  // Same as Received.js: debounced PO-level EPC validation
  useEffect(() => {
    if (!open || !poDbId) {
      setValidationResult(null);
      return undefined;
    }

    const uniqueTags = extractUniqueEpcTags(raw);
    if (uniqueTags.length === 0) {
      setValidationResult(null);
      return undefined;
    }

    const timeoutId = setTimeout(async () => {
      try {
        setIsValidating(true);

        if (isRealBox) {
          // Server-authoritative box classification
          const { data } = await axios.post('/api/v1/box-received-logs/validate-epcs', {
            box_id: Number(box.id),
            epc_hex_tags: uniqueTags,
          });
          const cls = data.classification || {};
          const newlyReceived = cls.newlyReceived || [];
          const foreignTags = cls.newlyReceivedOtherSku || [];
          setValidationResult({
            boxScan: true,
            totalScanned: data.totalCount ?? uniqueTags.length,
            totalMatched: newlyReceived.length,
            unmatched: (cls.notOnPo || []).length,
            unitMatched: newlyReceived.length,
            validEpcs: newlyReceived,
            byProduct: [],
            foreignProducts: (data.foreignProducts || []).map((p) => ({
              ...p,
              scanned_this_session: p.count,
            })),
            foreignTagCount: foreignTags.length,
            // Box-unknown tags are informational, not duplicate exceptions
            duplicates: (data.duplicates || []).filter(
              (d) => !(cls.receivedBoxUnknown || []).some((t) => t.hex_epc === d.hexEpc),
            ),
            alreadyInBox: (cls.alreadyInBox || []).length,
            receivedElsewhere: cls.receivedElsewhere || [],
            receivedBoxUnknown: cls.receivedBoxUnknown || [],
            notOnPo: (cls.notOnPo || []).length,
            previousScanned: data.previousScanned ?? 0,
            projectedScannedTotal: data.projectedScannedTotal ?? newlyReceived.length,
            expectedQuantity: data.expectedQuantity,
          });
          return;
        }

        const { data } = await axios.post('/api/v1/po-received-logs/validate-epcs', {
          po_id: poDbId,
          epc_hex_tags: uniqueTags,
        });

        // Prefer EPCs for this unit's product when we have one (box/container line)
        let unitMatched = data.totalMatched ?? 0;
        let unitEpcs = [];
        const byProduct = data.byProduct || [];
        if (box?.productId) {
          const row = byProduct.find((p) => String(p.product_id) === String(box.productId));
          if (row) {
            unitMatched = row.scanned_this_session ?? 0;
            unitEpcs = row.validEpcs || [];
          } else {
            unitMatched = 0;
            unitEpcs = [];
          }
        } else if (box?.sku) {
          const row = byProduct.find((p) => p.sku === box.sku);
          if (row) {
            unitMatched = row.scanned_this_session ?? 0;
            unitEpcs = row.validEpcs || [];
          } else {
            unitMatched = 0;
            unitEpcs = [];
          }
        } else {
          unitEpcs = byProduct.flatMap((p) => p.validEpcs || []);
        }

        setValidationResult({
          totalScanned: data.totalScanned ?? uniqueTags.length,
          totalMatched: data.totalMatched ?? 0,
          unmatched: data.unmatched ?? 0,
          unitMatched,
          validEpcs: unitEpcs,
          byProduct,
          foreignProducts: (box?.productId || box?.sku)
            ? byProduct.filter((p) => {
                const isTarget = box?.productId
                  ? String(p.product_id) === String(box.productId)
                  : p.sku === box.sku;
                return !isTarget && (p.scanned_this_session ?? 0) > 0;
              })
            : [],
          duplicates: data.duplicates || [],
        });
      } catch (err) {
        console.error('Failed to validate EPCs', err);
        setValidationResult(null);
      } finally {
        setIsValidating(false);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [raw, open, poDbId, box?.productId, box?.sku, box?.id, box?.inboundType, isRealBox]);

  const handleChange = (e) => {
    const value = e.target.value;
    setRaw(value);
    recompute(value);
  };

  // Count against this box/container expected uses product-scoped match (Received.js uses PO total)
  const matchedCount =
    validationResult != null
      ? (box?.productId || box?.sku
          ? validationResult.unitMatched
          : validationResult.totalMatched)
      : counts.unique;

  const epcsToSubmit =
    validationResult?.boxScan
      ? captured
      : validationResult?.validEpcs?.length > 0
        ? validationResult.validEpcs
        : extractUniqueEpcTags(raw);
  const duplicates = validationResult?.duplicates || [];
  const foreignProducts = validationResult?.foreignProducts || [];
  const foreignMatched = foreignProducts.reduce(
    (sum, product) => sum + (Number(product.scanned_this_session) || 0),
    0,
  );
  // For real boxes the server counts previous + newly stamped, not the
  // per-scan match — over-receipt is checked against the projected total.
  const selectedMatchedCount = validationResult?.boxScan
    ? validationResult.projectedScannedTotal ?? 0
    : allowMixedSku
      ? validationResult?.totalMatched ?? matchedCount
      : matchedCount;

  const buildPayload = ({ mixedOverride = allowMixedSku, overReceipt = false } = {}) => ({
    epcs: mixedOverride && validationResult && !validationResult.boxScan
      ? validationResult.byProduct.flatMap((product) => product.validEpcs || [])
      : epcsToSubmit,
    matchedCount: validationResult?.boxScan
      ? selectedMatchedCount
      : mixedOverride
        ? validationResult?.totalMatched ?? matchedCount
        : matchedCount,
    totalScanned: counts.unique,
    poMatched: validationResult?.totalMatched ?? matchedCount,
    duplicates,
    allowMixedSku: mixedOverride,
    allowOverReceipt: overReceipt,
  });

  const processScan = () => {
    if (validationResult?.boxScan && (validationResult.unitMatched ?? 0) <= 0 && (validationResult.foreignTagCount ?? 0) <= 0) return;
    if (!epcsToSubmit.length && matchedCount <= 0 && duplicates.length === 0) return;
    if (box && foreignMatched > 0 && !allowMixedSku) {
      setShowMixedConfirm(true);
      return;
    }
    if (box && selectedMatchedCount > box.expectedUnits) {
      setShowOverConfirm(true);
      return;
    }
    onSubmit(buildPayload());
  };

  const confirmOver = () => {
    onSubmit(buildPayload({ overReceipt: true }));
    setShowOverConfirm(false);
  };

  const keepSkuOnly = () => {
    setShowMixedConfirm(false);
    if (box && matchedCount > box.expectedUnits) {
      setShowOverConfirm(true);
      return;
    }
    onSubmit(buildPayload({ mixedOverride: false }));
  };

  const confirmMixed = () => {
    setAllowMixedSku(true);
    setShowMixedConfirm(false);
    const mixedTotal = validationResult?.boxScan
      ? validationResult.projectedScannedTotal ?? 0
      : validationResult?.totalMatched ?? matchedCount;
    if (box && mixedTotal > box.expectedUnits) {
      setShowOverConfirm(true);
      return;
    }
    onSubmit(buildPayload({ mixedOverride: true }));
  };

  if (!open) return null;

  const overBy = selectedMatchedCount - (box?.expectedUnits || 0);
  const boxScanNoNew =
    validationResult?.boxScan === true &&
    (validationResult.unitMatched ?? 0) <= 0 &&
    (validationResult.foreignTagCount ?? 0) <= 0;
  const canProcess =
    counts.unique > 0 &&
    !boxScanNoNew &&
    (validationResult == null ||
      matchedCount > 0 ||
      validationResult.totalMatched > 0 ||
      duplicates.length > 0);
  const firstDuplicate = duplicates[0];

  return (
    <>
      <div className="fixed inset-0 z-[120]">
        <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
        <div className="absolute inset-0 flex items-center justify-center p-4">
          <div className="flex max-h-[90vh] w-full max-w-lg flex-col rounded-lg bg-white shadow-2xl">
            <div className="border-b border-gray-200 px-5 py-4">
              <div className="flex items-center gap-2 justify-between">
                <div className="flex items-center gap-1">
                  <Barcode className="h-5 w-5 text-gray-600" />
                  <h3 className="text-base font-semibold text-gray-900">{copy.title}</h3>
                  {box && <span className='font-mono text-xs text-gray-500'>Box {box.boxNumber} · {box.sku}</span>}
                  {isContainer && box.containerName ? ` · ${box.containerName}` : ''}
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={onClose} className="p-1">
                    <XMarkIcon className="h-4 w-4 text-gray-600" />
                  </button>
                </div>
              </div>
              <p className="mt-2 text-xs leading-relaxed text-gray-500">{copy.description}</p>
            </div>

            <div className="flex min-h-0 flex-1 flex-col gap-3 px-5 py-4">
              <div className="flex flex-wrap items-baseline gap-2 text-xs text-gray-600">
                <span className="font-medium">Detected EPC tags:</span>
                <span className="inline-block rounded bg-blue-100 px-1.5 font-semibold text-blue-800 tabular-nums">
                  {counts.raw}
                </span>
                {counts.unique !== counts.raw && (
                  <span className="text-gray-500">(unique: {counts.unique})</span>
                )}
                {validationResult?.boxScan && (
                  <>
                    <span className="text-gray-400">→</span>
                    <span className="font-medium">New tags (this SKU):</span>
                    <span
                      className={`inline-block rounded px-1.5 font-semibold tabular-nums ${
                        validationResult.unitMatched > 0
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {validationResult.unitMatched}
                    </span>
                    {validationResult.foreignTagCount > 0 && (
                      <span className="font-medium text-amber-700">
                        · New tags (other SKUs): {validationResult.foreignTagCount}
                      </span>
                    )}
                    {validationResult.alreadyInBox > 0 && (
                      <span className="text-gray-600">
                        · Already counted in this box: {validationResult.alreadyInBox}
                      </span>
                    )}
                    {validationResult.receivedElsewhere.length > 0 && (
                      <span className="font-medium text-red-700">
                        · Received in another box: {validationResult.receivedElsewhere.length}{' '}
                        ({[...new Set(validationResult.receivedElsewhere.map((d) => d.locationLabel))].join(', ')})
                      </span>
                    )}
                    {validationResult.receivedBoxUnknown.length > 0 && (
                      <span className="text-gray-500">
                        · Already received on this PO; box unknown: {validationResult.receivedBoxUnknown.length}
                      </span>
                    )}
                    {validationResult.notOnPo > 0 && (
                      <span className="text-gray-500">· Not on this PO: {validationResult.notOnPo}</span>
                    )}
                    <span className="text-gray-700">
                      · Box count after scan: {validationResult.previousScanned} +{' '}
                      {validationResult.unitMatched} = {validationResult.projectedScannedTotal}
                    </span>
                    {boxScanNoNew && (
                      <span className="font-medium text-gray-700">
                        · No new tags — box count stays {validationResult.previousScanned}
                      </span>
                    )}
                  </>
                )}
                {validationResult && !validationResult.boxScan && (
                  <>
                    <span className="text-gray-400">→</span>
                    <span className="font-medium">Matched to PO:</span>
                    <span
                      className={`inline-block rounded px-1.5 font-semibold tabular-nums ${
                        validationResult.totalMatched > 0
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {validationResult.totalMatched}
                    </span>
                    {validationResult.unmatched > 0 && (
                      <span className="text-gray-500">
                        ({validationResult.unmatched} ignored — not on this PO)
                      </span>
                    )}
                    {(box?.productId || box?.sku) && (
                      <>
                        <span className="text-gray-400">·</span>
                        <span className="font-medium">This SKU:</span>
                        <span className="inline-block rounded bg-gray-100 px-1.5 font-semibold text-gray-800 tabular-nums">
                          {validationResult.unitMatched}
                        </span>
                      </>
                    )}
                    {foreignMatched > 0 && (
                      <span className="font-medium text-amber-700">
                        · Other SKUs: {foreignMatched}
                      </span>
                    )}
                  </>
                )}
                {isValidating && <span className="italic text-gray-500">Validating…</span>}
              </div>

              {firstDuplicate && (
                <div className="flex items-center gap-1.5 rounded bg-red-50 px-2 py-1.5 text-xs text-red-700">
                  <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate font-mono">{firstDuplicate.hexEpc}</span>
                  <span className="shrink-0">
                    already received in {firstDuplicate.locationLabel}
                    {duplicates.length > 1 ? ` (+${duplicates.length - 1} more)` : ''}
                  </span>
                </div>
              )}

              <div className="flex min-h-[200px] flex-col overflow-hidden rounded-md border border-gray-300 bg-gray-50">
                <div
                  ref={logRef}
                  className="min-h-0 flex-1 overflow-y-auto px-3 py-2 font-mono text-xs leading-5 text-gray-700"
                >
                  {captured.length === 0 ? (
                    <div className="flex flex-col gap-1 items-center justify-center">
                      <span className="text-gray-600">Point scanner at box and pull the trigger.</span>
                      <span className="text-gray-600">EPCs appear here as they stream in.</span>
                    </div>
                  ) : (
                    captured.map((epc, i) => (
                      <div key={`${epc}-${i}`} className="flex gap-2">
                        <span className="w-6 shrink-0 text-right text-gray-400 tabular-nums">
                          {i + 1}
                        </span>
                        <span>{epc}</span>
                      </div>
                    ))
                  )}
                </div>
                <textarea
                  ref={textareaRef}
                  value={raw}
                  onChange={handleChange}
                  placeholder="Tap here to paste EPCs..."
                  className="h-10 shrink-0 resize-none border-t border-gray-300 bg-white px-3 py-2 font-mono text-xs focus:outline-none focus:ring-1 focus:ring-gray-400"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-200 bg-gray-50 px-5 py-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={processScan}
                disabled={!canProcess}
                className="rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
              >
                {copy.submit}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showOverConfirm && (
        <div className="fixed inset-0 z-[130]">
          <div className="absolute inset-0 bg-black/50" aria-hidden="true" />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-2xl">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                <div>
                  <h4 className="font-semibold text-gray-900">Are you sure?</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    You are about to over-receive this box by{' '}
                    <strong>{overBy}</strong> unit{overBy === 1 ? '' : 's'} ({selectedMatchedCount} matched vs{' '}
                    {box?.expectedUnits} expected).
                    {foreignMatched > 0 && allowMixedSku && (
                      <> The count includes tags from {foreignProducts.map((p) => p.sku).join(', ')}.</>
                    )}
                  </p>
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowOverConfirm(false)}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                >
                  Go back
                </button>
                <button
                  type="button"
                  onClick={confirmOver}
                  className="rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700"
                >
                  Confirm over-receive
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showMixedConfirm && (
        <div className="fixed inset-0 z-[130]">
          <div className="absolute inset-0 bg-black/50" aria-hidden="true" />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-2xl">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                <div>
                  <h4 className="font-semibold text-gray-900">Tags from other SKUs detected</h4>
                  <p className="mt-1 text-sm text-gray-600">
                    This box is <strong>{box?.sku}</strong>. The scan matched{' '}
                    <strong>{matchedCount}</strong> of this SKU and also found{' '}
                    {foreignProducts.map((product, index) => (
                      <React.Fragment key={product.product_id || product.sku}>
                        {index > 0 ? ', ' : ''}
                        <strong>{product.scanned_this_session} {product.sku}</strong>
                      </React.Fragment>
                    ))}.
                  </p>
                  <p className="mt-2 text-sm text-gray-600">
                    The default keeps those other-SKU tags unreceived. Continue only if this is an intentional mixed box.
                  </p>
                </div>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={keepSkuOnly}
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm"
                >
                  Keep this SKU only
                </button>
                <button
                  type="button"
                  onClick={confirmMixed}
                  className="rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700"
                >
                  Confirm mixed box
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ScanCaptureDialog;
